import React, { useState, useEffect } from 'react';
import { getServicesByCategory, getAttributesForCategory } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SendMessageModal from '../components/SendMessageModal';
import './Books.css';

function Books() {
  const [bookingPackages, setBookingPackages] = useState([]);
  const [attributeNames, setAttributeNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Handle subscription button click
  const handleSubscribe = (packageId, packageTitle, hasTrialPeriod) => {
    // If user is not logged in, redirect to sign-in with return URL
    if (!currentUser) {
      const redirectUrl = `/subscribe?package=${packageId}&trial=${hasTrialPeriod ? '1' : '0'}`;
      navigate(`/signin?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }
    
    // If user is logged in but not verified
    if (currentUser && !currentUser.isVerified) {
      navigate('/verify-email');
      return;
    }
    
    // If user is logged in and verified, proceed to subscription page
    navigate(`/subscribe?package=${packageId}&trial=${hasTrialPeriod ? '1' : '0'}`);
  };

  // Handle Book a Call button click
  const handleBookCall = (pkg) => {
    setSelectedPackage(pkg);
    setShowModal(true);
  };

  // Close modal handler
  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Determine if package has a trial period
  const hasTrialPeriod = (pkg) => {
    const trialValue = pkg.attributes?.['Trial'];
    return trialValue && trialValue !== 'N/A' && trialValue !== '0';
  };

  useEffect(() => {
    // Track page view with GTM dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'page_view',
      page_title: 'Books - Bookkeeping Services',
      page_location: window.location.href,
      page_path: window.location.pathname
    });

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch Books services
        const servicesData = await getServicesByCategory('Books');
        setBookingPackages(servicesData);
        
        // Fetch attribute names for Books category
        const attributesData = await getAttributesForCategory('Books');
        setAttributeNames(attributesData.attribute_names || []);
        
        setLoading(false);
        setRetrying(false);
      } catch (error) {
        console.error('Error fetching bookkeeping data:', error);
        setError('Failed to load bookkeeping packages. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, [retrying]); // Re-run when retrying changes

  // Helper function to get cell value
  const getCellValue = (pkg, attributeName) => {
    const value = pkg.attributes?.[attributeName];
    
    // Special formatting for certain attribute types
    if (attributeName === 'Bank/CC Reconciliations' && value === 'Unlimited') {
      return 'Unlimited';
    }
    if (attributeName === 'Bank/CC Reconciliations' && value) {
      return `Up to ${value}`;
    }
    // if (attributeName === 'Dedicated Bookkeeper') {
    //   return value === 'Yes' ? 'Yes' : 'No';
    // }
    
    return value || 'N/A';
  };

  // Format trial value
  const formatTrialValue = (pkg) => {
    const trialValue = pkg.attributes?.['Trial'];
    if (!trialValue || trialValue === 'N/A' || trialValue === '0') {
      return '❌'; // X symbol
    }
    
    return `✅ ${trialValue} Month${trialValue === '1' ? '' : 's'} Trial*`;
  };

  // Check if Trial attribute exists
  const hasTrialAttribute = attributeNames.includes('Trial');

  // Filter out Trial from general attributes if it exists
  const regularAttributes = hasTrialAttribute 
    ? attributeNames.filter(name => name !== 'Trial')
    : attributeNames;

  // Check if price is custom/variable (not a fixed price)
  const isCustomPricing = (price) => {
    // Check if price contains "+", "custom", "varies", etc.
    return /\+|custom|varies|call|quote/i.test(price);
  };

  const handleRetry = () => {
    setRetrying(true);
  };

  return (
    <div className="service-page-container">
      <div className="services-page-hero enhanced">
        <div className="container">
          <h1>Smart, Scalable Bookkeeping — Built for Founders</h1>
          <p className="services-page-description">
            We combine AI-powered automation with human-verified accuracy to deliver stress-free, reliable bookkeeping tailored to your business stage.
          </p>
          
          <div className="services-intro">
            <p>Whether you're just starting out or scaling fast, our packages are designed to give you exactly what you need — and nothing you don't.</p>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="service-page-content">
          {/* Service features cards section */}
          <section className="services-features-grid">
            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon green">
                  <span className="icon">✓</span>
                </div>
                <h3>Accurate & Reliable Bookkeeping</h3>
              </div>
              <p>Manage finances effortlessly with Inkle Books, our modern accounting software backed by certified bookkeepers and accountants. Automate transactions, categorize expenses with AI, and keep accurate records.</p>
            </div>

            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon blue">
                  <span className="icon">📊</span>
                </div>
                <h3>Clean Books, Every Month</h3>
              </div>
              <p>Always up to date, always audit-ready. Our team ensures your financial records are accurate, organized, and prepared for investors or tax season.</p>
            </div>

            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon purple">
                  <span className="icon">💼</span>
                </div>
                <h3>Invoicing & Bill Pay Support</h3>
              </div>
              <p>Skip the busywork with streamlined invoicing systems and bill payment processes, complete with approval workflows and scheduled payments.</p>
            </div>

            <div className="services-card">
              <div className="services-card-header">
                <div className="services-card-icon gold">
                  <span className="icon">📈</span>
                </div>
                <h3>Custom Financial Reports</h3>
              </div>
              <p>From P&L to cash flow statements, get the financial insights you need when you need them with customizable dashboards and detailed reporting.</p>
            </div>
          </section>

          <section className="bookkeeping-packages">
            <h2>Our Packages</h2>
            {loading ? (
              <p>Loading packages...</p>
            ) : error ? (
              <div className="error-container">
                <p className="error-message">{error}</p>
                <button className="retry-button" onClick={handleRetry}>Retry</button>
              </div>
            ) : bookingPackages.length === 0 ? (
              <p>No bookkeeping packages available at this time.</p>
            ) : (
              <>
                {/* Desktop table view */}
                <div className="packages-table-container">
                  <table className="packages-table">
                    <thead>
                      <tr>
                        <th>Feature</th>
                        {bookingPackages.map(pkg => (
                          <th key={pkg.id}>{pkg.title}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="price-row">
                        <td className="feature-name">Price</td>
                        {bookingPackages.map(pkg => (
                          <td key={`${pkg.id}-price`} className="price-cell">{pkg.price}</td>
                        ))}
                      </tr>
                      {regularAttributes.map(attrName => (
                        <tr key={attrName}>
                          <td className="feature-name">{attrName}</td>
                          {bookingPackages.map(pkg => (
                            <td key={`${pkg.id}-${attrName}`}>
                              {getCellValue(pkg, attrName)}
                            </td>
                          ))}
                        </tr>
                      ))}
                      
                      {/* Trial row - only shown if Trial attribute exists */}
                      {hasTrialAttribute && (
                        <tr className="trial-row">
                          <td className="feature-name">Trial Period</td>
                          {bookingPackages.map(pkg => (
                            <td key={`${pkg.id}-trial`} className="trial-cell">
                              {formatTrialValue(pkg)}
                            </td>
                          ))}
                        </tr>
                      )}
                      
                      <tr className="subscribe-row">
                        <td></td>
                        {bookingPackages.map(pkg => (
                          <td key={`${pkg.id}-subscribe`}>
                            {isCustomPricing(pkg.price) ? (
                              <button 
                                className="book-a-call-button"
                                onClick={() => handleBookCall(pkg)}
                              >
                                Book a Call
                              </button>
                            ) : (
                              <button 
                                className="subscribe-button"
                                onClick={() => handleSubscribe(pkg.id, pkg.title, hasTrialPeriod(pkg))}
                              >
                                Subscribe
                              </button>
                            )}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                  {hasTrialAttribute && (
                    <p className="trial-footnote">Your card will not be charged during the trial period. You can cancel anytime before the trial ends at no cost.</p>
                  )}
                </div>

                {/* Mobile cards view */}
                <div className="mobile-packages">
                  {bookingPackages.map(pkg => (
                    <div key={pkg.id} className="mobile-package-card">
                      <div className="mobile-package-header">
                        {pkg.title}
                      </div>
                      <table className="mobile-package-features">
                        <tbody>
                          {regularAttributes.map(attrName => (
                            <tr key={`mobile-${pkg.id}-${attrName}`}>
                              <td>{attrName}</td>
                              <td>{getCellValue(pkg, attrName)}</td>
                            </tr>
                          ))}
                          
                          {/* Trial row - only shown if Trial attribute exists */}
                          {hasTrialAttribute && (
                            <tr className="mobile-package-trial">
                              <td>Trial Period</td>
                              <td>{formatTrialValue(pkg)}</td>
                            </tr>
                          )}
                          
                          {/* Price row moved below trial */}
                          <tr className="mobile-package-price-row">
                            <td>Price</td>
                            <td className="mobile-price-value">{pkg.price}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="mobile-package-footer">
                        {isCustomPricing(pkg.price) ? (
                          <button 
                            className="contact-button"
                            onClick={() => handleBookCall(pkg)}
                          >
                            Book a Call
                          </button>
                        ) : (
                          <button 
                            className="subscribe-button"
                            onClick={() => handleSubscribe(pkg.id, pkg.title, hasTrialPeriod(pkg))}
                          >
                            Subscribe
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {hasTrialAttribute && (
                    <p className="trial-footnote">Your card will not be charged during the trial period. You can cancel anytime before the trial ends at no cost.</p>
                  )}
                </div>
              </>
            )}
          </section>
          
          <section className="tools-flexibility">
            <h2>Flexible Tools for Your Workflow</h2>
            <p>Our adaptive approach to bookkeeping means you can choose what works best for your business.</p>
            
            <div className="tools-options">
              <div className="tool-option">
                <div className="tool-option-icon">
                  <span>🔄</span>
                </div>
                <h3>Use Our Tools</h3>
                <p>We provide modern, AI-powered accounting software that streamlines your financial processes with minimal effort.</p>
              </div>
              
              <div className="tool-option">
                <div className="tool-option-icon">
                  <span>⚙️</span>
                </div>
                <h3>Use Your Tools</h3>
                <p>If you prefer your existing software, our skilled team can work directly with QuickBooks, Xero, or any platform you already use.</p>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Send Message Modal */}
      {showModal && (
        <SendMessageModal
          formName={selectedPackage?.title || 'Bookkeeping'}
          onClose={handleCloseModal}
          showDatePicker={true}
        />
      )}
    </div>
  );
}

export default Books; 